import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, Upload } from 'lucide-react';
import { InputSwitch } from 'primereact/inputswitch';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import {
  useGetDesignerExemplarQuery,
  useReExtractDesignerExemplarMutation,
  useUploadDesignerExemplarImageMutation,
} from 'src/redux/operations/operations.endpoints';
import type { CanvasClass } from '../types/creative-template.types';
import type {
  DesignerCanvasClass,
  DesignerExemplar,
  DesignerExemplarWritePayload,
} from '../types/designer-exemplar.types';
import {
  applyDesignerExtractEnqueue,
  buildDesignerExemplarPayload,
  formatExtractedStructureSummary,
  isDisplayableImageUrl,
  isSceneGraph,
  mergeDesignerCommitResult,
  parseDesignerExemplarMutationError,
  validateDesignerExemplarForm,
} from '../helpers/designer-exemplar.helpers';
import { CANVAS_CLASS_OPTIONS } from '../types/creative-template.types';
import { COMMON_VERTICAL_OPTIONS } from '../types/designer-exemplar.types';
import { SceneGraphPreview } from './scene-graph-preview';
import { DesignerExemplarStatusBadge } from './designer-exemplar-status-badge';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';

const textareaClassName = cn(
  'flex min-h-[96px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors',
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
);

interface DesignerExemplarFormProps {
  initialExemplar?: DesignerExemplar | null;
  canvasClasses: DesignerCanvasClass[] | undefined;
  isCreate: boolean;
  isSaving?: boolean;
  isRetiring?: boolean;
  submitError?: string | null;
  onSubmit: (values: DesignerExemplarWritePayload) => void | Promise<void | DesignerExemplar>;
  onCancel?: () => void;
  onRetire?: () => void | Promise<void>;
  onExemplarUpdated?: (exemplar: DesignerExemplar) => void;
}

export function DesignerExemplarForm({
  initialExemplar,
  canvasClasses,
  isCreate,
  isSaving = false,
  isRetiring = false,
  submitError = null,
  onSubmit,
  onCancel,
  onRetire,
  onExemplarUpdated,
}: DesignerExemplarFormProps) {
  const [name, setName] = useState(initialExemplar?.name ?? '');
  const [canvasClass, setCanvasClass] = useState<CanvasClass>(
    initialExemplar?.canvas_class ?? 'panel_square',
  );
  const [vertical, setVertical] = useState(initialExemplar?.vertical ?? '');
  const [customVertical, setCustomVertical] = useState('');
  const [notes, setNotes] = useState(initialExemplar?.notes ?? '');
  const [active, setActive] = useState(initialExemplar?.active ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [ratioMismatch, setRatioMismatch] = useState(initialExemplar?.ratio_mismatch ?? false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialExemplar?.image_url ?? null);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputId = useId();
  const previewUrlRef = useRef<string | null>(previewUrl);
  const localPreviewRef = useRef<string | null>(null);
  previewUrlRef.current = previewUrl;

  const [requestUpload] = useUploadDesignerExemplarImageMutation();
  const [reExtract, { isLoading: isReExtracting }] = useReExtractDesignerExemplarMutation();

  const exemplarId = initialExemplar?.id ?? '';

  const { data: polledExemplar, refetch } = useGetDesignerExemplarQuery(
    { exemplarId },
    { skip: !exemplarId },
  );

  const currentExemplar = polledExemplar ?? initialExemplar ?? null;

  useEffect(() => {
    if (!exemplarId) {
      return undefined;
    }
    const status = currentExemplar?.extraction_status;
    if (status !== 'running' && status !== 'pending') {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      void refetch();
    }, 3000);
    return () => window.clearInterval(intervalId);
  }, [currentExemplar?.extraction_status, exemplarId, refetch]);

  useEffect(() => {
    if (polledExemplar) {
      onExemplarUpdated?.(polledExemplar);
      setRatioMismatch(polledExemplar.ratio_mismatch);
      setPreviewUrl((current) => {
        if (current?.startsWith('blob:')) {
          return current;
        }
        if (isDisplayableImageUrl(polledExemplar.image_url)) {
          return polledExemplar.image_url;
        }
        return polledExemplar.image_url ?? current;
      });
    }
  }, [onExemplarUpdated, polledExemplar]);

  useEffect(() => {
    return () => {
      const url = localPreviewRef.current ?? previewUrlRef.current;
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  const canvasClassOptions = useMemo(() => {
    if (canvasClasses?.length) {
      return canvasClasses.filter((entry) => entry.active);
    }
    return CANVAS_CLASS_OPTIONS.map((slug) => ({
      slug,
      display_name: slug.replace(/_/g, ' '),
      reference_width: 0,
      reference_height: 0,
      tolerance: 0,
      active: true,
    }));
  }, [canvasClasses]);

  const verticalValue = vertical === '__custom__' ? customVertical : vertical;

  const enqueueExtraction = useCallback(async(
    exemplarId: string,
    base: DesignerExemplar | null
  ) => {
    try {
      const enqueue = await reExtract({ exemplarId }).unwrap();
      return applyDesignerExtractEnqueue(base, enqueue);
    } catch (error) {
      setUploadError(
        error instanceof Error
          ? error.message
          : parseDesignerExemplarMutationError(error) || 'Extraction could not be started.'
      );
      return base;
    }
  }, [reExtract]);

  const uploadImage = useCallback(async(
    file: File,
    exemplarIdOverride?: string,
    baseExemplar?: DesignerExemplar | null
  ) => {
    const exemplarId = exemplarIdOverride ?? currentExemplar?.id;
    if (!exemplarId) {
      setPendingFile(file);
      return null;
    }

    setUploadError(null);
    setUploadProgress(0);

    try {
      const committed = await requestUpload({
        exemplarId,
        file,
      }).unwrap();

      const merged = mergeDesignerCommitResult(
        baseExemplar ?? (currentExemplar?.id === exemplarId ? currentExemplar : null),
        committed
      );
      setRatioMismatch(Boolean(merged?.ratio_mismatch));
      const withExtract = await enqueueExtraction(exemplarId, merged);
      if (withExtract) {
        onExemplarUpdated?.(withExtract);
      }
      setUploadProgress(null);
      void refetch();
      return withExtract;
    } catch (error) {
      setUploadProgress(null);
      setUploadError(
        error instanceof Error
          ? error.message
          : parseDesignerExemplarMutationError(error),
      );
      return null;
    }
  }, [currentExemplar, enqueueExtraction, onExemplarUpdated, refetch, requestUpload]);

  const setPreviewFromFile = (file: File) => {
    setPreviewFailed(false);
    setPreviewUrl((current) => {
      if (current?.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      const next = URL.createObjectURL(file);
      localPreviewRef.current = next;
      return next;
    });
  };

  const handleFileSelection = (file: File | null) => {
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file.');
      return;
    }
    setUploadError(null);
    setPreviewFromFile(file);
    if (currentExemplar?.id) {
      setPendingFile(null);
      void uploadImage(file);
      return;
    }
    setPendingFile(file);
  };

  const handleSubmit = async(event: React.FormEvent) => {
    event.preventDefault();
    const payload = buildDesignerExemplarPayload({
      name,
      canvas_class: canvasClass,
      vertical: verticalValue,
      notes,
      active,
    });
    const error = validateDesignerExemplarForm(payload);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    const saved = await onSubmit(payload);
    if (!isCreate) {
      return;
    }
    if (pendingFile && saved?.id) {
      const committed = await uploadImage(pendingFile, saved.id, saved);
      setPendingFile(null);
      if (!committed) {
        onExemplarUpdated?.(saved);
      }
      return;
    }
    if (saved) {
      onExemplarUpdated?.(saved);
    }
  };

  const handleReExtract = async() => {
    if (!currentExemplar?.id) {
      return;
    }
    const updated = await enqueueExtraction(currentExemplar.id, currentExemplar);
    if (updated) {
      onExemplarUpdated?.(updated);
    }
    void refetch();
  };

  const extractedStructure = currentExemplar?.extracted_structure ?? null;
  const displayError = validationError ?? submitError ?? uploadError;

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {displayError ? (
        <div className="rounded-md border border-red-101 bg-red-101 p-3 text-sm text-red-700" role="alert">
          {displayError}
        </div>
      ) : null}

      {!isCreate ? (
        <div className="rounded-md border border-gray-300 bg-gray-50 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <DesignerExemplarStatusBadge status={currentExemplar?.extraction_status} />
            <SchedulerDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={currentExemplar?.extraction_status === 'running' || isReExtracting}
              onClick={() => {
                void handleReExtract();
              }}
            >
              <RefreshCw className={isReExtracting ? 'animate-spin' : undefined} />
              Re-extract
            </SchedulerDsButton>
          </div>
          {currentExemplar?.extraction_status === 'pending' ? (
            <p className="mt-3 text-sm text-gray-600">
              Extraction is queued in Studio. It stays pending until a Studio worker picks up the task.
            </p>
          ) : null}
        </div>
      ) : null}

      <div
        className={`rounded-md border-2 border-dashed p-4 ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        }`}
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          handleFileSelection(event.dataTransfer.files?.[0] ?? null);
        }}
      >
        <input
          ref={fileInputRef}
          id={fileInputId}
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={uploadProgress !== null || isSaving}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            handleFileSelection(event.target.files?.[0] ?? null);
            event.target.value = '';
          }}
        />
        {previewUrl ? (
          <div className="space-y-3">
            <img
              src={previewUrl}
              alt="Exemplar preview"
              className="max-h-64 rounded-md border border-gray-300"
              onLoad={() => {
                setPreviewFailed(false);
                const localPreview = localPreviewRef.current;
                if (localPreview && previewUrl !== localPreview) {
                  URL.revokeObjectURL(localPreview);
                  localPreviewRef.current = null;
                }
              }}
              onError={() => {
                const localPreview = localPreviewRef.current;
                if (localPreview && previewUrl !== localPreview) {
                  setPreviewUrl(localPreview);
                  return;
                }
                setPreviewFailed(true);
              }}
            />
            {previewFailed ? (
              <p className="text-sm text-amber-900">
                The file uploaded, but Studio&apos;s stored image URL is not readable in the browser.
                Extraction can still run if Studio&apos;s worker can reach the object.
              </p>
            ) : null}
            <SchedulerDsButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={uploadProgress !== null || isSaving}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="size-4" />
              Replace image
            </SchedulerDsButton>
          </div>
        ) : (
          <label
            htmlFor={fileInputId}
            className={cn(
              'flex w-full cursor-pointer flex-col items-center gap-2 py-8 text-sm text-gray-600',
              (uploadProgress !== null || isSaving) && 'pointer-events-none cursor-not-allowed opacity-50',
            )}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Upload className="size-5" />
            Drag and drop an image, or click to browse.
          </label>
        )}
        {uploadProgress !== null ? (
          <div className="mt-3 text-sm text-gray-600">Uploading…</div>
        ) : null}
        {ratioMismatch ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>
              Image dimensions are outside the tolerance for this canvas class. The exemplar was saved,
              but ops should review before using it in production.
            </span>
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1 md:col-span-2">
          <span className={labelClassName}>Name</span>
          <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Hero left, CTA bottom" />
          <p className="text-xs text-gray-500">
            Describe the layout, not the brand. Example: &quot;Split hero with bottom CTA bar&quot;.
          </p>
        </label>

        <label className="space-y-1">
          <span className={labelClassName}>Canvas class</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={canvasClass}
            onChange={(event) => setCanvasClass(event.target.value as CanvasClass)}
          >
            {canvasClassOptions.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.display_name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className={labelClassName}>Vertical</span>
          <select
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            value={vertical}
            onChange={(event) => setVertical(event.target.value)}
          >
            <option value="">Select vertical</option>
            {COMMON_VERTICAL_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          {vertical === '__custom__' ? (
            <Input
              className="mt-2"
              value={customVertical}
              onChange={(event) => setCustomVertical(event.target.value)}
              placeholder="Enter vertical"
            />
          ) : null}
        </label>

        <label className="space-y-1 md:col-span-2">
          <span className={labelClassName}>Notes</span>
          <textarea
            className={textareaClassName}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Guidance for Remi at runtime — what makes this layout worth emulating?"
          />
          <p className="text-xs text-gray-500">
            Notes are read by the agent at runtime. Write them as guidance to a designer, not metadata.
          </p>
        </label>

        <div className="flex items-center gap-3 md:col-span-2">
          <InputSwitch checked={active} onChange={(event) => setActive(event.value)} />
          <span className="text-sm text-gray-700">Active</span>
        </div>
      </div>

      {!isCreate ? (
        <section className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
          <h3 className="text-sm font-medium text-black">Extracted structure</h3>
          {extractedStructure && isSceneGraph(extractedStructure) ? (
            <SceneGraphPreview sceneGraph={extractedStructure} />
          ) : null}
          <pre className="max-h-48 overflow-auto rounded-md border border-gray-300 bg-white p-3 text-xs text-gray-800">
            {formatExtractedStructureSummary(extractedStructure)}
          </pre>
        </section>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <SchedulerDsButton type="submit" disabled={isSaving || uploadProgress !== null}>
          {isSaving || uploadProgress !== null ? <Loader2 className="size-4 animate-spin" /> : null}
          {isCreate ? 'Create exemplar' : 'Save changes'}
        </SchedulerDsButton>
        {onCancel ? (
          <SchedulerDsButton type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </SchedulerDsButton>
        ) : null}
        {!isCreate && currentExemplar?.active && onRetire ? (
          <SchedulerDsButton
            type="button"
            variant="danger-ghost"
            disabled={isRetiring}
            onClick={() => {
              void onRetire();
            }}
          >
            Retire exemplar
          </SchedulerDsButton>
        ) : null}
      </div>
    </form>
  );
}
